import createClient from "openapi-fetch";
import type { components, paths } from "../../shared/api/schema";
import type { AppPasswordScope } from "../../shared/constants";
import type { SocialProviderId } from "../../shared/social";

export const api = createClient<paths>({
  baseUrl: "",
  credentials: "include",
});

export type Me = components["schemas"]["Me"];
export type ResumeSummary = components["schemas"]["ResumeSummary"];
export type ResumeDocument = components["schemas"]["ResumeDocument"];
export type ResumeTheme = components["schemas"]["ResumeTheme"];
export type ResumeLayout = components["schemas"]["ResumeLayout"];
export type ResumeProfile = components["schemas"]["ResumeProfile"];
export type Share = components["schemas"]["Share"];
export type Billing = components["schemas"]["Billing"];
export type AppPassword = components["schemas"]["AppPassword"];
export type Asset = components["schemas"]["Asset"];
export type PublicResume = components["schemas"]["PublicResume"];
export type Organization = components["schemas"]["Organization"];
export type OrganizationSummary = components["schemas"]["OrganizationSummary"];
export type OrganizationInvitation = components["schemas"]["OrganizationInvitation"];
export type OrganizationMember = components["schemas"]["OrganizationMember"];
export type OrganizationRole = components["schemas"]["OrganizationSummary"]["role"];
export type InvitationInfo = components["schemas"]["InvitationInfo"];

export type SectionKey =
  | "education"
  | "experience"
  | "projects"
  | "links"
  | "skills"
  | "languages"
  | "certifications"
  | "awards";

export type SectionItem = ResumeDocument[SectionKey][number];

export class ApiClientError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;

  constructor(message: string, status: number, code: string, details?: unknown) {
    super(message);
    this.name = "ApiClientError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

interface ErrorBody {
  error?: { code?: string; message?: string; details?: unknown };
}

function toError(status: number, body: unknown): ApiClientError {
  const envelope = body as ErrorBody | undefined;
  const code = envelope?.error?.code ?? "INTERNAL";
  const message = envelope?.error?.message ?? "Request failed";
  return new ApiClientError(message, status, code, envelope?.error?.details);
}

type Result<T> = {
  data?: T;
  error?: unknown;
  response: Response;
};

export function unwrap<T>(result: Result<T>): T {
  if (!result.response.ok || result.error !== undefined) {
    throw toError(result.response.status, result.error);
  }
  return result.data as T;
}

async function send<T>(method: string, path: string, body?: unknown): Promise<T> {
  const response = await fetch(path, {
    method,
    credentials: "include",
    headers: body === undefined ? undefined : { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const text = await response.text();
  const data: unknown = text ? JSON.parse(text) : null;
  if (!response.ok) throw toError(response.status, data);
  return data as T;
}

export const endpoints = {
  me: async () => unwrap(await api.GET("/api/me")),

  authConfig: async () => send<{ socialProviders: SocialProviderId[] }>("GET", "/api/auth-config"),

  updateMe: async (input: { name?: string; locale?: "en" | "vi" }) =>
    unwrap(await api.PATCH("/api/me", { body: input })),

  deleteMe: async () => unwrap(await api.DELETE("/api/me")),

  exportMe: async () => {
    const response = await fetch("/api/me/export", { credentials: "include" });
    if (!response.ok) throw toError(response.status, await response.json());
    return response.json() as Promise<unknown>;
  },

  resumes: async () => unwrap(await api.GET("/api/resumes")),

  createResume: async (input: { title: string; templateId?: string; locale?: "en" | "vi" }) =>
    unwrap(await api.POST("/api/resumes", { body: input })),

  resume: async (id: string) =>
    unwrap(await api.GET("/api/resumes/{id}", { params: { path: { id } } })),

  updateResume: async (
    id: string,
    input: {
      title?: string;
      slug?: string;
      locale?: "en" | "vi";
      templateId?: string;
    },
  ) => unwrap(await api.PATCH("/api/resumes/{id}", { params: { path: { id } }, body: input })),

  deleteResume: async (id: string) =>
    unwrap(await api.DELETE("/api/resumes/{id}", { params: { path: { id } } })),

  duplicateResume: async (id: string) =>
    unwrap(await api.POST("/api/resumes/{id}/duplicate", { params: { path: { id } } })),

  document: async (id: string) =>
    unwrap(await api.GET("/api/resumes/{id}/document", { params: { path: { id } } })),

  updateProfile: async (
    id: string,
    input: Partial<ResumeProfile> & { avatarAssetId?: string | null },
  ) =>
    unwrap(
      await api.PUT("/api/resumes/{id}/profile", {
        params: { path: { id } },
        body: input,
      }),
    ),

  updateTheme: async (id: string, input: Partial<ResumeTheme>) =>
    unwrap(
      await api.PUT("/api/resumes/{id}/theme", {
        params: { path: { id } },
        body: input as never,
      }),
    ),

  updateLayout: async (id: string, input: Partial<ResumeLayout>) =>
    unwrap(
      await api.PUT("/api/resumes/{id}/layout", {
        params: { path: { id } },
        body: input as never,
      }),
    ),

  share: async (id: string) =>
    unwrap(await api.GET("/api/resumes/{id}/share", { params: { path: { id } } })),

  publish: async (id: string, visibility: "unlisted" | "public") =>
    unwrap(
      await api.POST("/api/resumes/{id}/publish", {
        params: { path: { id } },
        body: { visibility },
      }),
    ),

  unpublish: async (id: string) =>
    unwrap(await api.POST("/api/resumes/{id}/unpublish", { params: { path: { id } } })),

  appPasswords: async () => unwrap(await api.GET("/api/app-passwords")),

  createAppPassword: async (input: {
    name: string;
    scopes: AppPasswordScope[];
    expiresInDays?: number;
  }) => unwrap(await api.POST("/api/app-passwords", { body: input })),

  deleteAppPassword: async (id: string) =>
    unwrap(await api.DELETE("/api/app-passwords/{id}", { params: { path: { id } } })),

  billing: async () => unwrap(await api.GET("/api/billing")),

  checkout: async (interval: "month" | "year") =>
    unwrap(await api.POST("/api/billing/checkout", { body: { interval } })),

  portal: async () => unwrap(await api.POST("/api/billing/portal")),

  publicResume: async (slug: string) =>
    unwrap(await api.GET("/api/public/resumes/{slug}", { params: { path: { slug } } })),

  organizations: async () => unwrap(await api.GET("/api/organizations")),

  createOrganization: async (input: { name: string }) =>
    unwrap(await api.POST("/api/organizations", { body: input })),

  organization: async (id: string) =>
    unwrap(await api.GET("/api/organizations/{id}", { params: { path: { id } } })),

  updateOrganization: async (id: string, input: { name: string }) =>
    unwrap(await api.PATCH("/api/organizations/{id}", { params: { path: { id } }, body: input })),

  deleteOrganization: async (id: string) =>
    unwrap(await api.DELETE("/api/organizations/{id}", { params: { path: { id } } })),

  leaveOrganization: async (id: string) =>
    unwrap(await api.POST("/api/organizations/{id}/leave", { params: { path: { id } } })),

  inviteMember: async (id: string, input: { email: string; role: "admin" | "member" }) =>
    unwrap(
      await api.POST("/api/organizations/{id}/invitations", {
        params: { path: { id } },
        body: input,
      }),
    ),

  revokeInvitation: async (id: string, invitationId: string) =>
    unwrap(
      await api.DELETE("/api/organizations/{id}/invitations/{invitationId}", {
        params: { path: { id, invitationId } },
      }),
    ),

  updateMember: async (id: string, memberId: string, role: OrganizationRole) =>
    unwrap(
      await api.PATCH("/api/organizations/{id}/members/{memberId}", {
        params: { path: { id, memberId } },
        body: { role },
      }),
    ),

  removeMember: async (id: string, memberId: string) =>
    unwrap(
      await api.DELETE("/api/organizations/{id}/members/{memberId}", {
        params: { path: { id, memberId } },
      }),
    ),

  organizationCheckout: async (id: string, input: { interval: "month" | "year"; seats: number }) =>
    unwrap(
      await api.POST("/api/organizations/{id}/billing/checkout", {
        params: { path: { id } },
        body: input,
      }),
    ),

  organizationPortal: async (id: string) =>
    unwrap(
      await api.POST("/api/organizations/{id}/billing/portal", {
        params: { path: { id } },
      }),
    ),

  invitationInfo: async (token: string) =>
    unwrap(await api.GET("/api/invitations/{token}", { params: { path: { token } } })),

  acceptInvitation: async (token: string) =>
    unwrap(await api.POST("/api/invitations/{token}/accept", { params: { path: { token } } })),

  section: <T>(id: string, section: SectionKey) => send<T>("GET", `/api/resumes/${id}/${section}`),

  createSectionItem: <T>(id: string, section: SectionKey, input: unknown) =>
    send<{ item: T }>("POST", `/api/resumes/${id}/${section}`, input),

  updateSectionItem: <T>(id: string, section: SectionKey, itemId: string, input: unknown) =>
    send<{ item: T }>("PATCH", `/api/resumes/${id}/${section}/${itemId}`, input),

  deleteSectionItem: (id: string, section: SectionKey, itemId: string) =>
    send<{ ok: true }>("DELETE", `/api/resumes/${id}/${section}/${itemId}`),

  reorderSection: <T>(id: string, section: SectionKey, ids: string[]) =>
    send<{ items: T[] }>("PUT", `/api/resumes/${id}/${section}/order`, { ids }),
};

export async function uploadAsset(file: File, kind: "avatar" | "project"): Promise<Asset> {
  const body = new FormData();
  body.append("file", file);
  body.append("kind", kind);
  const response = await fetch("/api/assets", {
    method: "POST",
    credentials: "include",
    body,
  });
  const data: unknown = await response.json().catch(() => null);
  if (!response.ok) throw toError(response.status, data);
  return data as Asset;
}

export function isUnauthorized(error: unknown): boolean {
  return error instanceof ApiClientError && error.status === 401;
}
